import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { NavLink, useLocation } from "react-router-dom";
import { MenuIcon, XCircle } from "lucide-react";
import icon from "../../../assets/apple-touch-icon.png";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsOpen(!isOpen);

  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const navLinkClass = (path: string) => {
    const baseClass =
      "relative px-3 py-2 text-sm font-medium transition-colors duration-200";
    const activeClass = "text-orange-500 font-semibold";
    const inactiveClass = "text-gray-700 hover:text-red-500";

    return `${baseClass} ${
      location.pathname === path ? activeClass : inactiveClass
    }`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
    exit: {
      opacity: 0,
      transition: { staggerChildren: 0.05, staggerDirection: -1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
    exit: { y: -20, opacity: 0 },
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`sticky top-0 z-50 w-full transition-all duration-300 px-10  ${
        scrolled ? "bg-white/80 backdrop-blur-sm shadow-sm" : "bg-orange-100/10"
      }`}
    >
      <div className="py-4">
        <div className="flex items-center justify-between">
          {/* Logo and Left Navigation */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="flex items-center space-x-6"
          >
            {/* Logo */}
            <div className="flex items-center space-x-1">
              <img
                src={icon}
                alt="icon"
                className="h-10 w-10 text-orange-500"
              />
              <h3 className="text-xl font-bold">Vaspay</h3>
            </div>

            {/* Left Navigation - Hidden on Mobile */}
            <motion.div
              variants={itemVariants}
              className="hidden md:flex items-center space-x-4 capitalize"
            >
              <NavLink to="/why-vaspay" className={navLinkClass("/why-vaspay")}>
                why Vaspay
              </NavLink>
              <NavLink to="/pricing" className={navLinkClass("/pricing")}>
                pricing
              </NavLink>
            </motion.div>
          </motion.div>

          {/* Desktop Right Navigation */}
          <motion.nav
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="hidden md:flex items-center space-x-8 capitalize"
          >
            <motion.div
              variants={itemVariants}
              className="flex items-center space-x-6"
            >
              <NavLink to="/developers" className={navLinkClass("/developers")}>
                developers
              </NavLink>
              <NavLink to="/support" className={navLinkClass("/support")}>
                support
              </NavLink>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="flex items-center space-x-4"
            >
              <NavLink to="/login" className={navLinkClass("/login")}>
                login
              </NavLink>
              <NavLink
                to="/register"
                className="px-4 py-2 rounded-md bg-red-500 text-white font-medium hover:bg-red-600 transition-colors duration-200"
              >
                register
              </NavLink>
            </motion.div>
          </motion.nav>

          {/* Mobile Menu Button */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleMenu}
            className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <XCircle className="h-6 w-6" />
            ) : (
              <MenuIcon className="h-6 w-6" />
            )}
          </motion.button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="md:hidden overflow-hidden"
            >
              <motion.nav
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="flex flex-col py-4 space-y-4"
              >
                <motion.div
                  variants={itemVariants}
                  className="flex flex-col space-y-3"
                >
                  <NavLink
                    to="/why-vaspay"
                    className={navLinkClass("/why-vaspay")}
                    onClick={() => setIsOpen(false)}
                  >
                    why Vaspay
                  </NavLink>
                  <NavLink
                    to="/pricing"
                    className={navLinkClass("/pricing")}
                    onClick={() => setIsOpen(false)}
                  >
                    pricing
                  </NavLink>
                  <NavLink
                    to="/developers"
                    className={navLinkClass("/developers")}
                    onClick={() => setIsOpen(false)}
                  >
                    developers
                  </NavLink>
                  <NavLink
                    to="/support"
                    className={navLinkClass("/support")}
                    onClick={() => setIsOpen(false)}
                  >
                    support
                  </NavLink>
                </motion.div>

                <motion.div
                  variants={itemVariants}
                  className="flex flex-col space-y-3 pt-2 border-t border-gray-200"
                >
                  <NavLink
                    to="/login"
                    className={navLinkClass("/login")}
                    onClick={() => setIsOpen(false)}
                  >
                    login
                  </NavLink>
                  <NavLink
                    to="/register"
                    className="px-4 py-2 rounded-md bg-orange-500 text-white font-medium hover:bg-orange-600 transition-colors duration-200 w-full text-center"
                    onClick={() => setIsOpen(false)}
                  >
                    register
                  </NavLink>
                </motion.div>
              </motion.nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
